// 11 §8 — the encounter's side lists in the mockup's shape (who has locked is on the Lock plan
// button, ActionQueue.tsx): FOES with their declared intent, PLAN · PULSE N with a rail per player, the
// OBJECTIVE (rule strip, ≤ 4 lines + regard lines), the compact view panel and playback controls.
import { useState } from 'react';
import { Badge, Button, ButtonRow, HeroSprite, HotkeyKbd, IconButton, Segmented } from '../components';
import { useAuthoring } from '../../core/stores/authoring';
import { useView, type Filter } from '../../core/stores/view';
import { usePlayback } from '../../core/stores/playback';
import { useEncounter, useShownSnapshot } from '../../core/encounter/session';
import { useEncounterRow, useOpeningStory, useNamedCharacters, useEncounterSecured } from '../../core/subscriptions/hooks';
import { rpc } from '../../core/net/reducers';
import { moveLines, submergedOn } from '../../core/sim/water';
import { playingMs } from '../../core/playback/plan';
import { BONUS_MARK } from './slots';
import { SLOTS, type EnemyDeclaration, type GoalView, type ProjectedBoard, type Snapshot, type UnitState } from '../../core/sim/types';
import type { BattleBriefView } from '../../core/story/genesis';
import { describeIntent, humanize, useSeats } from './shared';
import { MindCard, MoodMark, Situations, TacticTag } from './Minds';
import { StatusChips } from './StatusChips';
import { LostFoes, useNoFoeInSight } from './Fog';
import { ariaKeys, bindingsOf, chordLabel, keyLabel, useHotkeyOverrides, withKey, type Overrides } from '../../core/input/hotkeys';

/** Every pan key, by direction: `W A S D, ↑ ← ↓ →`. */
const panKeys = (o: Overrides): string => {
  const ids = ['camera.panUp', 'camera.panLeft', 'camera.panDown', 'camera.panRight'];
  const rows = [0, 1].map((n) => ids.map((id) => bindingsOf(id, o)[n]).filter((c): c is string => !!c).map(chordLabel).join(' ')).filter(Boolean);
  return rows.join(', ');
};
import { pendingLine, reserveLines } from '../../core/encounter/calls';
import { SIDE_LINE } from '../../core/explore/explore';

const REASONS: Record<string, string> = {
  path_blocked: 'path blocked', target_dead: 'target already dead', destination_taken: 'destination taken',
  out_of_range: 'out of range', illegal: 'illegal', moved_by_enemy: 'moved by an enemy',
};

export function ConflictList() {
  const projected = useAuthoring((s) => s.projected);
  const heroNames = useEncounter((s) => s.heroNames);
  const conflicts = projected?.conflicts ?? [];
  const ordering = projected?.ordering ?? [];
  if (!conflicts.length && !ordering.length) return null;
  const heroName = (id: number) => heroNames[id] ?? `hero ${id}`;
  return (
    <ul className="fk-hud__flags" data-testid="conflict-list" aria-label="Conflicts and ordering notes">
      {conflicts.map((c, i) => <li key={i} className={`fk-hud__flag fk-hud__flag--${c.severity}`}><b>{c.severity === 'red' ? '!' : '?'}</b>{heroName(c.intent.heroId)} {c.intent.slot}: {REASONS[c.reason] ?? c.reason}{c.at ? ` at (${c.at.x},${c.at.y})` : ''}{c.detail ? ` — ${c.detail}` : ''}</li>)}
      {ordering.map((o, i) => <li key={`o${i}`} className="fk-hud__flag fk-hud__flag--note"><b>①→②</b>{o.stage}: {o.why}</li>)}
    </ul>
  );
}

/**
 * How this plan ends the fight, if it does and the party can know it. Under fog a leader out of
 * sight is not on the party's board, so "beat the leader" holds for nothing and every empty plan
 * read as Victory; a kill objective is only won by a plan that drops someone, and the leader's
 * only by one that can see the leader.
 */
export function planEnding(projected: ProjectedBoard | undefined, snapshot: Snapshot | undefined): string | undefined {
  const ended = projected?.ended;
  if (!ended || !projected || !snapshot) return ended;
  const goals = snapshot.goals?.goals ?? [];
  if (ended !== 'Victory' || !snapshot.fog || !goals.some((g) => g.kind === 'BreakLeader' || g.kind === 'Rout')) return ended;
  const foe = (id: number) => snapshot.units.find((u) => u.unitId === id)?.faction === 'enemy';
  const drops = projected.units.some((u) => u.dead && foe(u.unitId) && !snapshot.units.find((x) => x.unitId === u.unitId)?.downed);
  const leaderSeen = snapshot.units.some((u) => u.leader && u.faction === 'enemy' && u.onBoard !== false);
  if (!drops || (goals.some((g) => g.kind === 'BreakLeader') && !leaderSeen)) return undefined;
  return ended;
}

/** The room's leader: the named character's name when the encounter has one, else the leader unit's. */
export function useLeader(): { name: string; unit?: UnitState } {
  const snapshot = useEncounter((s) => s.snapshot);
  const encounterId = useEncounter((s) => s.encounterId);
  const worldId = useEncounter((s) => s.worldId);
  const enc = useEncounterRow(encounterId);
  const named = useNamedCharacters(worldId);
  const leader = enc?.leaderNpc !== undefined && enc.leaderNpc !== null ? named.find((n) => n.npc === enc.leaderNpc) : undefined;
  const unit = snapshot?.leaderUnit !== undefined ? snapshot.units.find((u) => u.unitId === snapshot.leaderUnit) : undefined;
  // docs/118 PT6: a crawl's dweller is out of the party's view until it is found; its kit still
  // carries its name, so the objective names the foe that waits below, never a stand-in
  return { name: leader?.name ?? unit?.name ?? snapshot?.bossName ?? 'The leader', ...(unit ? { unit } : {}) };
}

/** A foe's declared intent in words: "Strike Jo for 2", "Holds", "Area, 4 tiles". */
export function intentLine(d: EnemyDeclaration | undefined, units: readonly UnitState[] | undefined): string {
  if (!d) return 'acts after your party';
  if (d.held) return 'Holds';
  const name = (u: number) => units?.find((x) => x.unitId === u)?.name ?? `#${u}`;
  const dmg = (u: number) => d.projected.filter(([t]) => t === u).reduce((s, [, n]) => s + n, 0);
  switch (d.aim.kind) {
    case 'unit': { const n = dmg(d.aim.target); return `${d.actionName} ${name(d.aim.target)}${n > 0 ? ` for ${n}` : ''}`; }
    case 'region': { const n = d.projected.reduce((s, [, v]) => s + v, 0); return `${d.actionName}, ${d.aim.tiles.length} tiles${n > 0 ? `, ${n} dmg` : ''}`; }
    case 'tile': return `${d.actionName} at (${d.aim.tile.x},${d.aim.tile.y})`;
    case 'none': return d.stance ? `${d.actionName} · ${humanize(d.stance)}` : d.actionName;
  }
}

/**
 * FOES · N: portrait, name (with a mood mark), the tactics card and declared intent, HP (now →
 * after this plan). Click picks the foe as a target while aiming; otherwise it opens the foe's
 * mind (docs/30) under its row.
 */
export function FoeList() {
  const [open, setOpen] = useState<{ unit: number; at: DOMRect } | undefined>(undefined);
  // docs/56: the foes as the pulse being played leaves them; the next pulse's intents once it opens
  const snapshot = useShownSnapshot();
  const playing = usePlayback((s) => s.playing);
  const planned = useAuthoring((s) => s.projected);
  const projected = playing ? undefined : planned;
  const targeting = useAuthoring((s) => s.machineState.kind === 'ActionChosen');
  const clickTile = useAuthoring((s) => s.clickTile);
  const foes = (snapshot?.units ?? []).filter((u) => u.faction === 'enemy' && u.onBoard !== false);
  const decl = new Map((projected?.declarations ?? []).map((d) => [d.actor, d]));
  const tactics = new Map((projected?.declarations ?? []).filter((d) => d.tactic).map((d) => [d.actor, d.tactic]));
  const minds = new Map((snapshot?.minds ?? []).map((m) => [m.unit, m]));
  const standing = foes.filter((f) => !f.downed).length;
  const noneInSight = useNoFoeInSight(standing);
  return (
    <section className="fk-hud__box fk-hud__foes" aria-label="Foes">
      <h3 className="fk-hud__label">{noneInSight ? 'Foes · none in sight' : snapshot?.fog ? `Foes in sight · ${standing}` : `Foes · ${standing}`}</h3>
      <ul className="fk-hud__rows" data-testid="foe-list">
        {foes.map((f) => {
          const pu = projected?.units.find((u) => u.unitId === f.unitId);
          const d = decl.get(f.unitId);
          const mind = minds.get(f.unitId);
          const expanded = open?.unit === f.unitId && mind !== undefined;
          // docs/46 T2: the HP it has now; what the plan would take off shows on the target preview
          const hp = <><b>{f.hp}</b>/{f.hpMax}</>;
          return (
            <li key={f.unitId}>
              <button type="button" className={`fk-hud__foe fk-hit ${f.downed ? 'fk-hud__foe--down' : ''} ${targeting ? 'fk-hud__foe--pick' : ''} ${expanded ? 'fk-hud__foe--open' : ''}`}
                onClick={(e) => { if (targeting) clickTile(f.tile); else if (mind) setOpen(expanded ? undefined : { unit: f.unitId, at: e.currentTarget.getBoundingClientRect() }); }}
                aria-expanded={mind ? expanded : undefined}
                title={[targeting ? `Target ${f.name}` : mind ? `${f.name}: ${mind.kin}, ${mind.wits.name.toLowerCase()} — click for its mind` : `${f.name} at (${f.tile.x},${f.tile.y})`, ...(snapshot ? moveLines(f, submergedOn(snapshot.board, f)) : [])].join('\n')} data-testid="foe" data-unit-id={f.unitId}>
                <span className="fk-hud__foe-face"><HeroSprite frame={f.appearance} scale={2} /></span>
                <span className="fk-hud__foe-text">
                  <span className="fk-hud__foe-name">{f.leader && <span className="fk-hud__crown" aria-label="leader">♛</span>}{f.name}{mind && <MoodMark mood={mind.mood} />}<StatusChips statuses={f.statuses} px={14} /></span>
                  {(() => { const lines = snapshot ? moveLines(f, submergedOn(snapshot.board, f)) : []; return lines.length ? <span className="fk-hud__foe-move" data-testid="foe-move">{lines.join(' ')}</span> : null; })()}
                  <span className="fk-hud__foe-intent">{f.downed ? 'down' : pu?.dead ? 'your plan drops it' : <><TacticTag tactic={tactics.get(f.unitId)} spell={mind?.focus?.intent} />{intentLine(d, snapshot?.units)}</>}</span>
                </span>
                <span className="fk-hud__foe-hp">{hp}</span>
              </button>
              {expanded && mind && <MindCard mind={mind} anchor={open?.at} />}
            </li>
          );
        })}
      </ul>
      <LostFoes />
      <LeaderReserve snapshot={snapshot} />
      <Situations minds={snapshot?.minds} />
    </section>
  );
}

/** docs/66 RM11: everything scheduled and shown, with its countdown — a call or a raising first. */
export function ComingLines({ snapshot }: { snapshot: Snapshot | undefined }) {
  if (!snapshot?.pending?.length) return null;
  const urgent = (k: string) => (k === 'Call' || k === 'Raise' ? 0 : 1);
  const coming = [...snapshot.pending].sort((a, b) => urgent(a.kind) - urgent(b.kind) || a.firesAt - b.firesAt);
  return (
    <>
      {coming.map((p) => (
        <li key={`p${p.card}`} className={`fk-rulestrip__coming fk-rulestrip__coming--${p.kind === 'Call' ? 'call' : p.kind === 'Raise' ? 'raise' : 'event'}`} data-testid="pending-line" data-kind={p.kind}>
          {pendingLine(p, snapshot.pulseIndex, snapshot)}
        </li>
      ))}
    </>
  );
}

/**
 * docs/66 RM10, RM11: the leader's card shows what can still come — its reserve, its raisings, a
 * second form — so the party knows before the first pulse how many more there can be.
 */
export function LeaderReserve({ snapshot }: { snapshot: Snapshot | undefined }) {
  if (!snapshot) return null;
  const lines = reserveLines(snapshot);
  if (!lines.length) return null;
  return (
    <ul className="fk-hud__reserve" data-testid="leader-reserve" aria-label="What the leader still has">
      {lines.map((l) => <li key={l}>{l}</li>)}
    </ul>
  );
}

/** PLAN · PULSE N: every hero's slots as one line each, railed in the owner's colour. */
export function PlanList() {
  const snapshot = useEncounter((s) => s.snapshot);
  const owners = useEncounter((s) => s.owners);
  const pulseIndex = useEncounter((s) => s.snapshot?.pulseIndex);
  const local = useAuthoring((s) => s.local);
  const rows = useAuthoring((s) => s.rows);
  const projected = useAuthoring((s) => s.projected);
  const { colour } = useSeats();
  const heroes = (snapshot?.units ?? []).filter((u) => u.heroId !== undefined && u.onBoard !== false);
  const lines: Array<{ key: string; who: string; owner?: string; text: string; state: string }> = [];
  for (const h of heroes) {
    const owner = owners[h.heroId!];
    let any = false, allHold = true, committed = false;
    const mine: typeof lines = [];
    for (const s of SLOTS) {
      const key = `${h.heroId}:${s}`;
      const it = local.get(key) ?? rows.get(key);
      if (!it) continue;
      any = true;
      const state = local.has(key) ? 'local' : rows.get(key)?.state ?? '';
      if (state === 'committed') committed = true;
      if (it.action === 2) continue;
      allHold = false;
      const eff = projected?.effects.find((e) => e.id.heroId === h.heroId && e.id.slot === s);
      const tail = eff?.kind === 'attack' ? `, ${eff.damage} damage` : eff?.kind === 'support' && eff.amount !== undefined ? `, +${eff.amount}` : '';
      mine.push({ key, who: h.name, owner, text: `${describeIntent(it.action, it.args, snapshot?.units, h)}${tail}${s === 'B' ? ` ${BONUS_MARK}` : ''}`, state });
    }
    // a hero with nothing planned yet has no line (the Lock plan button says who is still planning)
    if (!any) continue;
    else if (allHold) lines.push({ key: `${h.heroId}:hold`, who: h.name, owner, text: committed ? 'holds' : 'holds so far', state: committed ? 'committed' : '' });
    else lines.push(...mine);
  }
  return (
    <section className="fk-hud__box fk-hud__plan" aria-label="Plan">
      <h3 className="fk-hud__label">Plan · turn {pulseIndex ?? '…'}</h3>
      {!lines.length && <span className="fk-hud__hint">Nothing planned yet.</span>}
      <ul className="fk-hud__rows" data-testid="plan-list">
        {lines.map((l) => (
          <li key={l.key} className={`fk-hud__planline fk-hud__planline--${l.state || 'none'}`} style={{ ['--fk-seat' as string]: colour(l.owner) }}>
            <b>{l.who}</b><span>{l.text}</span>{l.state === 'committed' && <span className="fk-hud__planlock" aria-label="committed">✓</span>}
          </li>
        ))}
      </ul>
      <ConflictList />
    </section>
  );
}

/** docs/138 XO3: the line once the wonder is found. */
export const FOUND_LINE = 'The wonder is found. Explore on, search for hidden loot, then leave when everyone is ready.';
const wonderFound = (snap: Snapshot) => !!snap.explore?.wonders.some((w) => w.found);
const foundLine = (snap: Snapshot) => (wonderFound(snap) ? 'The wonder is found' : 'Find the wonder');

/**
 * docs/50 SB2: the objective's aims as lines, named as the story named them ("Take up the fallen
 * star", "Hold the sluice house — 1 of 3 pulses"), then how it is lost short of a wipe.
 */
export function goalLines(snap: Snapshot, leaderName: string, brief?: BattleBriefView): string[] {
  const g = snap.goals;
  if (!g || !g.goals.length) return [];
  const fixture = (id?: number) => snap.fixtures?.find((f) => f.id === id);
  const unitName = (id?: number) => snap.units.find((u) => u.unitId === id)?.name ?? 'them';
  const place = snap.fixtures?.find((f) => f.kind === 'Landmark')?.name || brief?.set_piece.landmark?.name || 'the marked ground';
  const thing = brief?.set_piece.thing;
  const verb = thing && 'Task' in thing ? thing.Task.verb : undefined;
  const line = (a: GoalView): string => {
    const f = fixture(a.fixture);
    // a relic still in its chest is not on the party's board; the story still names it
    const what = f?.name ?? (thing && 'Relic' in thing ? thing.Relic.name : 'it');
    switch (a.kind) {
      case 'Rout': return 'Rout them all';
      case 'BreakLeader': return `Beat or drive off ${leaderName}`;
      case 'Hold': return `Hold ${place}: ${a.value ?? 0} of ${a.of ?? 0} turns`;
      case 'Reach': return `Reach ${place}`;
      case 'Escort': return `Bring ${unitName(a.unit)} to ${place}`;
      case 'Survive': return `Stand until turn ${a.of ?? 0}`;
      case 'Take': return f?.holder !== undefined ? `${unitName(f.holder)} has ${what}` : `Take up ${what}`;
      case 'Deliver': return f?.holder !== undefined ? `${unitName(f.holder)} carries ${what} to ${place}` : `Take up ${what} and carry it to ${place}`;
      case 'Work': return `${verb ?? 'Work at'} ${what}: ${a.value ?? 0} of ${a.of ?? 0}`;
      case 'Solve': return 'Open the way';
      // docs/61 EX3: nothing has to be fought; the party chooses where to go on
      case 'Depart': {
        const ways = (snap.explore?.ways ?? []).filter((w) => w.sighted && w.name).map((w) => w.name);
        return ways.length ? `See what there is to see, then gather on a way out to go on (${ways.join(', ')})` : 'See what there is to see, then gather on a way out into the mist to go on';
      }
      case 'Find': return foundLine(snap);
    }
  };
  // docs/138 XO3: an exploration's objective, then its side objective (once found, one line says both)
  const findOnly = g.goals.length === 1 && g.goals[0]!.kind === 'Find';
  const aims = g.goals.map(line);
  const out = findOnly
    ? (wonderFound(snap) ? [FOUND_LINE] : ['Find the wonder.', SIDE_LINE])
    : g.goals.length > 1 ? [`${g.join === 'all' ? 'All of' : 'Either'}: ${aims.join(g.join === 'all' ? '; ' : '; or ')}.`] : [`${aims[0]}.`];
  for (const l of snap.losses ?? []) {
    if (l.kind === 'Dropped') out.push(`Lost if ${unitName(l.unit)} falls.`);
    // docs/138 XO1: a found wonder is the win; the turn limit only closes the secured field
    else if (findOnly && wonderFound(snap)) continue;
    else {
      const left = l.pulse - snap.pulseIndex + 1;
      out.push(left > 0 ? `Lost if not done by turn ${l.pulse} (${left} left).` : `Lost at the end of this turn.`);
    }
  }
  return out;
}

/** OBJECTIVE: the rule strip (objective, the leader's order, flee, plan-ends) + ledger regard lines. */
export function RuleStrip() {
  const snapshot = useEncounter((s) => s.snapshot);
  const encounterId = useEncounter((s) => s.encounterId);
  const controlled = useEncounter((s) => s.controlled);
  const projected = useAuthoring((s) => s.projected);
  const enc = useEncounterRow(encounterId);
  const leaderName = useLeader().name;
  const story = useOpeningStory(encounterId);
  const lines: string[] = [];
  if (story) lines.push(story.battle.objective);
  // a quest's fight carries its own line (docs/50 §6)
  // docs/138 XO3: an exploration says its own objective (the briefing told the story)
  else if (snapshot?.stakes && !snapshot.explore) lines.push(snapshot.stakes);
  // docs/50: every aim the sim keeps, by the names the story gave them, with its count
  const aims = snapshot ? goalLines(snapshot, leaderName, story?.battle.brief) : [];
  if (aims.length) lines.push(...aims);
  else if (snapshot?.objective) lines.push(`${humanize(snapshot.objective)}.`);
  // docs/61: an exploration has no leader giving orders
  if (snapshot?.order && !snapshot.explore) lines.push(`${leaderName} orders: ${humanize(snapshot.order)}.`);
  if (snapshot?.fleeCalled) lines.push('Flee called: reach the western edge.');
  // docs/25 D1: on a secured field the fight is already won; no plan "ends" it again
  const secured = !!useEncounterSecured(encounterId);
  const ending = secured ? undefined : planEnding(projected, snapshot);
  const endLine = ending ? `This plan ends the fight: ${humanize(ending)}.` : undefined;
  if (endLine) lines.push(endLine);
  // 08 §11 / 11 §10.7: ≤ 4 relationship lines from the ledger, in the server's fixed order.
  const regard = (enc?.regardStrip ?? []).slice(0, 4);

  return (
    <section className="fk-hud__box fk-hud__objective" aria-label="Objective">
      <h3 className="fk-hud__label">Objective</h3>
      <ul className="fk-rulestrip" data-testid="rule-strip">
        {lines.slice(0, 7).map((l) => <li key={l} className={l === endLine ? 'fk-rulestrip__ends' : undefined}>{l}</li>)}
        <ComingLines snapshot={snapshot} />
        {regard.map((l, i) => <li key={`r${i}`} className="fk-rulestrip__regard" data-testid="regard-line">{l}</li>)}
      </ul>
      {snapshot?.pendingSurrender && encounterId && (
        <ButtonRow className="fk-hud__surrender">
          <Badge tone="amber" pulse>surrender offered</Badge>
          <Button small primary disabled={!controlled.length} onClick={() => void rpc.acceptSurrender(encounterId)}>Accept</Button>
          <Button small disabled={!controlled.length} onClick={() => void rpc.refuseSurrender(encounterId)}>Refuse</Button>
        </ButtonRow>
      )}
    </section>
  );
}

const FILTERS: Array<{ id: Filter; label: string; title: string }> = [{ id: 'mine', label: 'Mine', title: 'My heroes' }, { id: 'wing', label: 'Wing', title: 'My wing' }, { id: 'attacks', label: 'Hits', title: 'Attacks only' }, { id: 'all', label: 'All', title: 'All intents' }];

/** View: intent filter (F), paths, minimap (M), zoom, the board-mark legend. */
export function FilterBar() {
  const keys = useHotkeyOverrides();
  const zin = keyLabel('camera.zoomIn', keys), zout = keyLabel('camera.zoomOut', keys);
  const zhold = [keyLabel('camera.zoomOutHold', keys), keyLabel('camera.zoomInHold', keys)].filter(Boolean).join(' / ');
  const filter = useView((s) => s.filter);
  const setFilter = useView((s) => s.setFilter);
  const zoom = useView((s) => s.zoomGoal);
  const zoomBy = useView((s) => s.zoomBy);
  const frameBoard = useView((s) => s.frameBoard);
  const hidePaths = useView((s) => s.hidePaths);
  const setHidePaths = useView((s) => s.setHidePaths);
  const minimap = useView((s) => s.minimap);
  const toggleMinimap = useView((s) => s.toggleMinimap);
  return (
    <section className="fk-hud__box fk-hud__view" aria-label="Filters and view">
      <h3 className="fk-hud__label">View <HotkeyKbd id="board.filter" /></h3>
      <div className="fk-hud__filters" role="group" aria-label="Intent filters">
        {FILTERS.map((f) => <button key={f.id} type="button" className="fk-hud__seg fk-hit" aria-pressed={filter === f.id} title={f.title} onClick={() => setFilter(f.id)}>{f.label}</button>)}
      </div>
      <div className="fk-hud__toggles">
        <button type="button" className="fk-hud__seg fk-hit" aria-pressed={hidePaths} title="Hide others' paths" onClick={() => setHidePaths(!hidePaths)}>Paths off</button>
        <button type="button" className="fk-hud__seg fk-hit" aria-pressed={minimap} onClick={toggleMinimap} aria-keyshortcuts={ariaKeys('board.minimap', keys)} title={withKey('Minimap', 'board.minimap', keys)}>Map</button>
      </div>
      <div className="fk-hud__toggles">
        <span className="fk-hud__label">Zoom</span>
        <span className="fk-hud__zoom" role="group" aria-label={`Zoom (mouse wheel, pinch${zhold ? `, ${zhold}` : ''}${zin || zout ? `, ${[zin, zout].filter(Boolean).join(' / ')}` : ''})`}>
          <IconButton small label={withKey('Zoom out', 'camera.zoomOut', keys)} onClick={() => zoomBy(-1)}>−</IconButton>
          <span className="fk-hud__zoom-value" data-testid="zoom">{Number(zoom.toFixed(1))}×</span>
          <IconButton small label={withKey('Zoom in', 'camera.zoomIn', keys)} onClick={() => zoomBy(1)}>+</IconButton>
        </span>
      </div>
      <div className="fk-hud__toggles">
        <button type="button" className="fk-hud__seg fk-hit" onClick={() => frameBoard('fit')} aria-keyshortcuts={ariaKeys('camera.fit', keys)} title={withKey('Fit the whole board', 'camera.fit', keys)}>Fit</button>
        <button type="button" className="fk-hud__seg fk-hit" onClick={() => frameBoard('hero')} aria-keyshortcuts={ariaKeys('camera.hero', keys)} title={`${withKey('Centre the selected hero', 'camera.hero', keys)} · pan with ${panKeys(keys) ? `${panKeys(keys)} or ` : ''}a drag`}>Hero</button>
      </div>
      <div className="fk-legend" aria-label="Board marks">
        <span className="fk-legend__exit">« exit</span>
        <span className="fk-legend__objective">○ goal</span>
      </div>
    </section>
  );
}

/** docs/83: the speeds and the "Played · 3.2 s" line are for `?debug=1`; players get Skip while a turn plays. */
export function PlaybackControls({ debug = false }: { debug?: boolean }) {
  const plan = usePlayback((s) => s.plan);
  const speed = usePlayback((s) => s.speed);
  const playing = usePlayback((s) => s.playing);
  const stageIndex = usePlayback((s) => s.stageIndex);
  const skip = usePlayback((s) => s.skip);
  const setSpeed = usePlayback((s) => s.setSpeed);
  const status = plan ? `${playing ? `Stage ${stageIndex + 1}/${plan.stages.length} · ${plan.stages[stageIndex]?.stage ?? ''}` : `Played · ${(playingMs(plan) / 1000).toFixed(1)} s`}${plan.autoSped ? ' · auto 2×' : ''}` : undefined;
  if (!debug) {
    return (
      <div className="fk-hud__playback" role="group" aria-label="Playback controls" data-testid="playback" data-playing={playing}>
        {playing && <Button small variant="ghost" onClick={skip}>Skip »</Button>}
      </div>
    );
  }
  return (
    <div className="fk-hud__playback" role="group" aria-label="Playback controls" data-testid="playback" data-playing={playing}>
      <Segmented label="Playback speed" value={speed} options={SPEEDS} onChange={setSpeed} />
      <Button small variant="ghost" onClick={skip} disabled={!playing}>Skip »</Button>
      <span className={`fk-hud__playback-status ${playing ? 'fk-hud__playback-status--live' : ''}`} data-testid="playback-status">{status ?? 'Replay waits for the first lock'}</span>
    </div>
  );
}

const SPEEDS: ReadonlyArray<{ value: 1 | 2 | 4; label: string }> = [{ value: 1, label: '1×' }, { value: 2, label: '2×' }, { value: 4, label: '4×' }];
